import { AxiosResponse } from 'axios'
import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { PBPLinks, PBPListResponse } from '@percent/cause-dashboard/services/types/pbpResponses.types'
import { APIErrorHandler } from '@percent/cause-dashboard/common/library/APIErrorHandler'
import { getQueryParamsFromUrl } from '@percent/cause-dashboard/common/utility/utility'

type PaginationProps = {
  pageSize?: number | null
}

const defaultPageData = { pageSize: 10 }

export const useQueryList = <A extends PaginationProps, B>(
  api: (_: A) => Promise<AxiosResponse<PBPListResponse<B>>>,
  initialQuery?: A,
  mount = true
) => {
  const { t } = useTranslation()
  const [body, setBody] = useState<PBPListResponse<B> | null>(null)
  const data = body?.data || null
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [status, setStatus] = useState<null | number>(null)
  const [paginationLinks, setPaginationLinks] = useState<PBPLinks | null>(null)
  const [totalPages, setTotalPages] = useState(0)
  const [totalResults, setTotalResultsNumber] = useState(0)
  const [onMount, setOnMount] = useState(mount)
  const [queryParams, setQueryParams] = useState<A>({ ...defaultPageData, ...initialQuery } as A)
  const query = useCallback((newQueryParams: A) => {
    setOnMount(true)
    setQueryParams({ ...defaultPageData, ...newQueryParams })
  }, [])
  const refresh = useCallback(() => {
    setQueryParams({ ...queryParams })
  }, [queryParams])
  const refreshToFirstPage = useCallback(() => {
    const { cursor, page, ...refreshParams } = queryParams as { [key: string]: string | number }
    setQueryParams(refreshParams as A)
  }, [queryParams])

  const nextPage = useCallback(() => {
    if (paginationLinks?.next) {
      setQueryParams(getQueryParamsFromUrl(paginationLinks.next))
    }
  }, [paginationLinks])
  const previousPage = useCallback(() => {
    if (paginationLinks?.prev) {
      setQueryParams(getQueryParamsFromUrl(paginationLinks.prev))
    }
  }, [paginationLinks])

  const fetchData = async () => {
    let didCancel = false
    setIsLoading(true)
    try {
      const { data: responseBody, status: apiStatus } = await api(queryParams)

      if (!didCancel) {
        setTotalPages(responseBody.totalResults ? Math.ceil(responseBody.totalResults / responseBody.pageSize) - 1 : 0)
        setTotalResultsNumber(responseBody?.totalResults || 0)

        if (apiStatus === 204 || responseBody?.data?.length === 0) {
          setErrorMessage(t('errorMessage.noResult'))
        } else {
          // eslint-disable-next-line no-underscore-dangle
          setPaginationLinks(responseBody._links)
        }
        setBody(responseBody)
        setStatus(apiStatus)
        setIsLoading(false)
        setErrorMessage('')
      }
    } catch (err: any) {
      if (!didCancel) {
        setErrorMessage(APIErrorHandler(err?.response?.apiData?.error))
        setError(err?.response?.data?.error)
        setIsLoading(false)
      }
    }

    return () => {
      didCancel = true
    }
  }

  useEffect(() => {
    if (onMount) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams])

  return [
    {
      data,
      isLoading,
      errorMessage,
      error,
      queryParams,
      totalPages,
      status,
      totalResults
    },
    {
      query,
      nextPage: paginationLinks?.next ? nextPage : null,
      previousPage: paginationLinks?.prev ? previousPage : null,
      refresh,
      refreshToFirstPage
    }
  ] as const
}
